import React from "react";
// import { useNavigate } from "react-router-dom"; // restore with the booking CTAs
import { Calendar, MapPin, Check, Clock, Presentation, Lightbulb } from "lucide-react";
import styles from "./EventHero.module.css";

/* Both are symposium sessions, so they share the same icon. */
const SESSION_ICONS = { presentation: Presentation, panel: Presentation };

/*
  EventHero
  ---------
  Hero banner for an event page: eyebrow + title + copy + single CTA on the
  left, a photo with a floating info card (dates / location / on-site team)
  on the right. Fully responsive — card stacks under the image on mobile.
*/
const EventHero = ({
  eyebrowCategory = "Event",
  eyebrowName = "American Academy of Optometry",
  title = ["Academy 2026", "Anaheim"],
  intro = "Join us for two AAO Lunch and Learn Symposia on September 30 and October 1",
  description = "Led by Mark Bullimore and Joseph Shovlin, the sessions will explore the landmark technology behind Deseyne®, the first and only FDA-cleared daily disposable soft contact lens for presbyopia with Extended Depth of Focus (EDOF)",
  ctaLabel = "Book a Fitting Slot",
  ctaSubtitle = "Reserve time with our team",
  onCtaClick,
  dates = "September 30 – October 3, 2026",
  location = "Anaheim, California",
  locationLine1 = "Anaheim Convention Center",
  locationLine2 = "Anaheim, California",
  teamNote = "Our clinical and technical specialists will be available throughout the event.",
  imageSrc = "/Images/anaheim-convention-center.png",
  imageAlt = "Anaheim Convention Center",

  expectEyebrow = "Upcoming Symposia",
  sessions = [
    {
      icon: "presentation",
      title: "Session 1",
      date: "Wednesday, September 30, 2026",
      time: "12:00 PM – 1:00 PM",
      location: ["Anaheim Convention Center", "North Level / 200", "Room 252C"],
      programTitle:
        "The Clinical Innovation Powering the First Daily Disposable Soft Contact Lens for Presbyopia with Extended Depth of Focus (EDOF)",
      summary: [
        "Join scientist and researcher Mark Bullimore, PhD, MCOptom, FAAO, for a presentation highlighting results from a recent clinical study published in Optometry and Vision Science.",
        "Dr. Bullimore will discuss the unique mechanism behind Deseyne®, the first and only FDA-cleared EDOF soft contact lens for presbyopia, and how it increased depth of focus while improving near and intermediate visual acuity and maintaining distance vision performance.",
      ],
    },
    {
      icon: "panel",
      title: "Session 2",
      // Wider label column on mobile so "Opportunity:" lines up with the rest.
      wideLabels: true,
      summaryLabel: "Session Description",
      opportunity: "Thursday Industry Innovation Lunch & Learns",
      date: "Thursday, October 1, 2026",
      time: "12:00 PM – 1:00 PM",
      location: ["Anaheim Convention Center",  "Room 253B"],
      programTitle:
        "Contact Lens Dropout, Refitting and Emerging Technologies",
      speakers:
        "Joseph Shovlin, OD, FAAO; Paul Karpecki, OD, FAAO; Michael DePaolis, OD, FAAO; Giancarlo Montani, DipOptom (It), FBCLA, FEAOO, FAAO",
      summary: [
        "Joseph P. Shovlin, OD, FAAO will lead a discussion among peers examining the persistent challenges of contact lens dropout, the opportunity to refit previously unsuccessful wearers, and emerging contact lens technologies such as the Deseyne® daily disposable soft contact lens for presbyopia with Extended Depth of Focus (EDOF).",
      ],
    },
  ],

  whyTitle = "Experience Deseyne® at Vision Expo 2027",
  whySubtitle = [
    "The Bruno Vision Care EDOF Experience",
    "Discover Deseyne®, the soft contact lens for presbyopia with Extended Depth of Focus.",
  ],
  whyPoints = [
    "Get a personalized fitting from one of our experts.",
    "Learn how EDOF technology differs from traditional multifocal optics.",
    "See how the unique Deseyne® fitting process can help reduce chair time.",
    "Meet our team, enjoy an espresso, and explore Deseyne® for your practice.",
  ],
  lensSrc = "/Images/Deseyne Logo.webp",
  brandLogoSrc = "/Images/Deseyne Logo.webp",

  bottomNote = "Space is limited. Reserve time with our team during Academy 2026.",
}) => {
  // Booking CTAs are commented out for now — restore these together with them.
  // const navigate = useNavigate();
  // Default: send the user to the Cal booking page; parent can override.
  // const handleCta = onCtaClick || (() => navigate("/academy-2026-anaheim/book-fitting-slot"));

  return (
    <div className={styles.eventHero}>
      <section className={styles.hero}>
        <div className={styles.inner}>
          {/* Left: copy + CTA */}
          <div className={styles.content}>
            <p className={styles.eyebrow}>
              <span>{eyebrowCategory}</span>
              <span className={styles.eyebrowDivider} aria-hidden="true" />
              <span>{eyebrowName}</span>
            </p>

            <h1 className={styles.title}>
              {title.map((line, i) => (
                <span className={styles.titleLine} key={i}>
                  {line}
                </span>
              ))}
            </h1>

            <p className={styles.intro}>{intro}</p>
            <p className={styles.description}>{description}</p>

            {/* Hero "Book a Fitting Slot" CTA — commented out for now.
            <div className={styles.ctaRow}>
              <button type="button" className={styles.ctaPrimary} onClick={handleCta}>
                <Calendar size={22} strokeWidth={2} />
                <span className={styles.ctaText}>
                  <span className={styles.ctaTitle}>{ctaLabel}</span>
                  <span className={styles.ctaSub}>{ctaSubtitle}</span>
                </span>
              </button>
            </div>
            */}

            <div className={styles.metaRow}>
              <span className={styles.metaItem}>
                <Calendar size={16} strokeWidth={2} />
                {dates}
              </span>
              <span className={styles.metaDivider} aria-hidden="true" />
              <span className={styles.metaItem}>
                <MapPin size={16} strokeWidth={2} />
                {location}
              </span>
            </div>
          </div>

          {/* Right: image + floating info card */}
          <div className={styles.visual}>
            <div className={styles.imageWrap}>
              <img src={imageSrc} alt={imageAlt} className={styles.image} />
            </div>

            {/* <aside className={styles.infoCard} aria-label="Event details">
              <div className={styles.infoBlock}>
                <div className={styles.infoIcon}>
                  <Calendar size={18} strokeWidth={2} />
                </div>
                <div>
                  <span className={styles.infoLabel}>Dates</span>
                  <p className={styles.infoText}>{dates}</p>
                </div>
              </div>

              <div className={styles.infoDivider} />

              <div className={styles.infoBlock}>
                <div className={styles.infoIcon}>
                  <MapPin size={18} strokeWidth={2} />
                </div>
                <div>
                  <span className={styles.infoLabel}>Location</span>
                  <p className={styles.infoText}>
                    {locationLine1}
                    <br />
                    {locationLine2}
                  </p>
                </div>
              </div>

              <div className={styles.infoDivider} />

              <div className={styles.teamBlock}>
                <span className={styles.infoLabelAccent}>Team on site</span>
                <p className={styles.infoText}>{teamNote}</p>
                <Users size={22} strokeWidth={1.6} className={styles.teamIcon} />
              </div>
            </aside> */}
          </div>
        </div>
      </section>

      {/* ---- Upcoming symposia ---- */}
      <section className={styles.expect}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionLine} aria-hidden="true" />
          <span className={styles.sectionEyebrow}>{expectEyebrow}</span>
          <span className={styles.sectionLine} aria-hidden="true" />
        </div>

        <div className={styles.sessionGrid}>
          {sessions.map((session, i) => {
            const Icon = SESSION_ICONS[session.icon] || Presentation;
            return (
              <article
                className={`${styles.sessionCard} ${session.wideLabels ? styles.wideLabels : ""}`}
                key={i}
              >
                <header className={styles.sessionHead}>
                  <span className={styles.sessionIcon}>
                    <Icon size={26} strokeWidth={1.8} />
                  </span>
                  <h3 className={styles.sessionTitle}>{session.title}</h3>
                </header>

                <dl className={styles.sessionMeta}>
                  <div className={styles.metaLine}>
                    <Calendar size={16} strokeWidth={2} className={styles.metaIco} />
                    <dt>Date:</dt>
                    <dd>{session.date}</dd>
                  </div>
                  <div className={styles.metaLine}>
                    <Clock size={16} strokeWidth={2} className={styles.metaIco} />
                    <dt>Time:</dt>
                    <dd>{session.time}</dd>
                  </div>
                  <div className={styles.metaLine}>
                    <MapPin size={16} strokeWidth={2} className={styles.metaIco} />
                    <dt>Location:</dt>
                    <dd>{session.location.join(", ")}</dd>
                  </div>
                  {session.opportunity && (
                    <div className={styles.metaLine}>
                      <Lightbulb size={16} strokeWidth={2} className={styles.metaIco} />
                      <dt>Opportunity:</dt>
                      <dd>{session.opportunity}</dd>
                    </div>
                  )}
                </dl>

                <hr className={styles.sessionRule} />

                <p className={styles.sessionProgram}>{session.programTitle}</p>

                {session.speakers && (
                  <p className={styles.sessionSpeakers}>
                    <span className={styles.summaryLabel}>Speaker(s): </span>
                    {session.speakers}
                  </p>
                )}

                <hr className={styles.sessionRule} />

                <div className={styles.sessionSummary}>
                  <span className={styles.summaryLabel}>
                    {session.summaryLabel || "Session Description"}:
                  </span>
                  {session.summary.map((para, j) => (
                    <p key={j}>{para}</p>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ---- Why visit (dark) ---- */}
      <section className={styles.why}>
        <div className={styles.whyInner}>
          <div className={styles.whyContent}>
            <h2 className={styles.whyTitle}>{whyTitle}</h2>
            {whySubtitle && (
              <p className={styles.whySubtitle}>
                {(Array.isArray(whySubtitle) ? whySubtitle : [whySubtitle]).map(
                  (line, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {line}
                    </React.Fragment>
                  )
                )}
              </p>
            )}
            <ul className={styles.whyList}>
              {whyPoints.map((point, i) => (
                <li key={i}>
                  <span className={styles.whyCheck}>
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.whyBrand}>
            {lensSrc && <img src={lensSrc} alt="" className={styles.whyLens} />}
            {brandLogoSrc && (
              <img src={brandLogoSrc} alt="Deseyne" className={styles.whyBrandLogo} />
            )}
          </div>
        </div>
      </section>

      {/* ---- Bottom CTA bar (separate, below the dark box) — commented out for now.
      <div className={styles.bottomBar}>
        <div className={styles.bottomInner}>
          <span className={styles.bottomNote}>
            <span className={styles.bottomIcon}>
              <Calendar size={18} strokeWidth={2} />
            </span>
            {bottomNote}
          </span>
          <button type="button" className={styles.ctaDark} onClick={handleCta}>
            <Calendar size={18} strokeWidth={2} />
            {ctaLabel}
          </button>
        </div>
      </div>
      ---- */}
    </div>
  );
};

export default EventHero;